import { z } from "zod";

/** The kinds the viewer can render, which is NOT the same set as the kinds a
 *  blob can be stored as (`BlobKind` in fp/blob.py, 19 members). The preview
 *  endpoint maps one to the other — pre-2007 Word renders through the Word
 *  viewer, a pasted note image through the MIME fallback — so this list is the
 *  contract for what may arrive on the wire, not an inventory of storage kinds.
 *
 *  Kept in lockstep with `ViewerKind` in fp-backend/fp/viewer_kind.py by
 *  ./viewerKindParity.test.ts, which imports this schema and parses that Python
 *  module. Adding a kind on either side without the other fails it (#11712). */
export const ViewerKindSchema = z.enum([
  "pdf",
  "email",
  "excel",
  // The backend has always served `fileType=csv`; without it here the parse
  // threw and the viewer had to be told the kind out-of-band. A chat-produced
  // file has no such out-of-band kind (#10988), so it must parse.
  "csv",
  "artifact",
  "onenote-page",
  "evestment-note",
  "teams-post",
  "docx",
  "pptx",
  "altvia_interaction",
  "altvia_interaction_note",
  "finpilot-interaction",
  // An agent's `.fp` meeting note (#12284): agent-written HTML, sandboxed.
  "agent-note",
  "html",
  "markdown",
  "unsupported",
  // A file whose bytes are not the format its name says (#11277): a
  // password-protected Office file, or a damaged or mislabelled one.
  "unreadable",
  "empty",
]);

export type ViewerKind = z.infer<typeof ViewerKindSchema>;

/** What the right-side viewer can be pointed at: a blob's kind, or the IODL
 *  result table (#10389), which is rendered from a citation payload rather than
 *  from a stored file. */
export type ViewerFileType = ViewerKind | "iodl-source";

export const FileViewerSchema = z.object({
  fileId: z.string(),
  fileName: z.string(),
  fileType: ViewerKindSchema,
});

export type FileViewerType = z.infer<typeof FileViewerSchema>;
